// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility for chronologically comparing asset events.
 * This module provides functionality to sort events based on their
 * occurrence dates and sequence numbers, handling null dates appropriately.
 */
import { Event } from '@/asset/api/types';

/**
 * Compares two events for chronological sorting.
 * Events are sorted by their occurrence dates in descending order (newest first).
 * If both events have the same date or null dates, they are sorted by their
 * sequence numbers in descending order (higher sequence numbers first).
 *
 * Sorting rules:
 * 1. Events with null dates are sorted after events with dates
 * 2. Events with the same date are sorted by sequence number
 * 3. Events with different dates are sorted chronologically
 *
 * @param event - First event to compare
 * @param other - Second event to compare
 * @returns Positive if event is earlier, negative if other is earlier
 */
export function compareEvents(event: Event, other: Event) {
  if (event.occurredAt === null && other.occurredAt === null) {
    return event.sequence < other.sequence ? 1 : -1;
  }

  if (event.occurredAt === null) {
    return 1;
  }

  if (other.occurredAt === null) {
    return -1;
  }

  if (event.occurredAt.equals(other.occurredAt)) {
    return event.sequence < other.sequence ? 1 : -1;
  }

  return event.occurredAt < other.occurredAt ? 1 : -1;
}
